/**
 * Meeting card in the mail reading pane (plan §3.1 "invites in the mail reading pane"): time, location, organizer
 * and Accept / Tentative / Decline for requests; a notice for cancellations; the answer for responses.
 */
import { useI18n } from '@platform/i18n';
import { Button, toast, useErrorText } from '@platform/ui';
import { CalendarIcon, CalendarXIcon, CheckIcon, HelpCircleIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, type MeetingInfo } from '../api';
import { RESPONSE_LABEL } from './events';
import { useCalendarFormat } from './format';
import { ProposalNotice } from './proposal-notice';
import { CalendarContext } from './calendar-context';
import { useContext } from 'react';

export interface MeetingCardProps {
  messageId: string;
  meeting: MeetingInfo;
  /** After an answer: reload the message (the card shows the new answer). */
  onAnswered: () => void;
}

export function MeetingCard({ messageId, meeting, onAnswered }: MeetingCardProps) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const zone = useContext(CalendarContext)?.zone;
  const errorText = useErrorText();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const start = meeting.start ? zone?.toWall(meeting.start) ?? new Date(meeting.start) : undefined;
  const end = meeting.end ? zone?.toWall(meeting.end) ?? new Date(meeting.end) : undefined;
  const answered = meeting.myResponse === 'accepted' || meeting.myResponse === 'declined' || meeting.myResponse === 'tentative';

  const respond = async (response: 'accept' | 'tentative' | 'decline') => {
    setBusy(true);
    try {
      await api.respondToEvent(messageId, { response, sendResponse: true });
      toast.success(t(`webmail.calendar.respondedToast.${response}`));
      onAnswered();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };

  const day = start ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}` : undefined;
  const heading =
    meeting.kind === 'cancellation'
      ? t('webmail.calendar.card.cancelled')
      : meeting.kind === 'response'
        ? t('webmail.calendar.card.response', { response: t(RESPONSE_LABEL[meeting.response ?? 'unknown']) })
        : t('webmail.calendar.card.request');

  return (
    <section aria-label={heading} className="mx-4 mb-2 flex flex-col gap-2 rounded-md border bg-muted/40 p-3 text-sm">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-primary [&_svg]:size-5">{meeting.kind === 'cancellation' ? <CalendarXIcon /> : <CalendarIcon />}</span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="font-semibold">{heading}</span>
          {start && end && (
            <>
              <span>{format.dayLabel(start)}</span>
              <span>{format.range(start, end, false)}</span>
            </>
          )}
          {meeting.location && <span className="text-muted-foreground">{meeting.location}</span>}
          {meeting.organizer && <span className="text-muted-foreground">{t('webmail.calendar.card.organizer', { name: meeting.organizer.name })}</span>}
          {meeting.kind === 'request' && answered && <span className="font-medium">{t('webmail.calendar.card.yourAnswer', { response: t(RESPONSE_LABEL[meeting.myResponse]) })}</span>}
        </div>
        {day && (
          <Button variant="ghost" size="sm" onClick={() => void navigate(`/calendar?date=${day}`)}>
            {t('webmail.calendar.card.openCalendar')}
          </Button>
        )}
      </div>
      {meeting.kind === 'request' && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy || meeting.myResponse === 'accepted'} onClick={() => void respond('accept')}>
            <CheckIcon />
            {t('webmail.calendar.accept')}
          </Button>
          <Button size="sm" variant="outline" disabled={busy || meeting.myResponse === 'tentative'} onClick={() => void respond('tentative')}>
            <HelpCircleIcon />
            {t('webmail.calendar.tentative')}
          </Button>
          <Button size="sm" variant="outline" disabled={busy || meeting.myResponse === 'declined'} onClick={() => void respond('decline')}>
            <XIcon />
            {t('webmail.calendar.decline')}
          </Button>
        </div>
      )}
      {meeting.kind === 'response' && <ProposalNotice messageId={messageId} eventId={meeting.eventId} />}
    </section>
  );
}
